import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Phone,
  FileCheck,
  Truck,
  CheckCircle,
  ClipboardCheck,
  Banknote,
  Package,
  Globe,
  ChevronRight,
  X,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { COMPANY_STATS } from "../constants/stats";
import ProcessStep from "../components/ProcessStep";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SellPanelsPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    panel_count: "",
    panel_brand: "",
    location: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    document.title = t("sellPanels.meta.title");
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", t("sellPanels.meta.description"));
    }
  }, [t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/enquiries/uk`, formData);
      toast.success(t("sellPanels.form.toastSuccess"));
      setFormData({
        name: "",
        company: "",
        phone: "",
        email: "",
        panel_count: "",
        panel_brand: "",
        location: "",
        message: "",
      });
    } catch (error) {
      toast.error(t("sellPanels.form.toastError"));
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const processSteps = [
    {
      icon: Phone,
      title: t("sellPanels.process.step1.title"),
      description: t("sellPanels.process.step1.description"),
    },
    {
      icon: ClipboardCheck,
      title: t("sellPanels.process.step2.title"),
      description: t("sellPanels.process.step2.description"),
    },
    {
      icon: Banknote,
      title: t("sellPanels.process.step3.title"),
      description: t("sellPanels.process.step3.description"),
    },
    {
      icon: FileCheck,
      title: t("sellPanels.process.step4.title"),
      description: t("sellPanels.process.step4.description"),
    },
    {
      icon: CheckCircle,
      title: t("sellPanels.process.step5.title"),
      description: t("sellPanels.process.step5.description"),
    },
    {
      icon: Truck,
      title: t("sellPanels.process.step6.title"),
      description: t("sellPanels.process.step6.description"),
    },
    {
      icon: Globe,
      title: t("sellPanels.process.step7.title"),
      description: t("sellPanels.process.step7.description"),
    },
  ];

  return (
    <div data-testid="sell-panels-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 bg-navy text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block px-4 py-2 bg-gold text-navy text-xs font-bold uppercase tracking-wider mb-6">
              {t("sellPanels.hero.badge")}
            </div>
            <h1 className="font-outfit text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              {t("sellPanels.hero.title")}<br />
              <span className="text-gold">{t("sellPanels.hero.titleAccent")}</span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl max-w-3xl mb-8">
              {t("sellPanels.hero.subtitle")}
            </p>
            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2 text-gold">
                <CheckCircle size={18} />
                <span>{t("sellPanels.hero.feature1")}</span>
              </div>
              <div className="flex items-center gap-2 text-gold">
                <CheckCircle size={18} />
                <span>{t("sellPanels.hero.feature2")}</span>
              </div>
              <div className="flex items-center gap-2 text-gold">
                <CheckCircle size={18} />
                <span>{t("sellPanels.hero.feature3")}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who We Buy From */}
      <section className="py-20 md:py-32">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
                {t("sellPanels.whoWeBuyFrom.kicker")}
              </span>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mt-4 mb-8">
                {t("sellPanels.whoWeBuyFrom.title")}
              </h2>

              <ul className="space-y-4">
                {[
                  t("sellPanels.whoWeBuyFrom.item1"),
                  t("sellPanels.whoWeBuyFrom.item2"),
                  t("sellPanels.whoWeBuyFrom.item3"),
                  t("sellPanels.whoWeBuyFrom.item4"),
                  t("sellPanels.whoWeBuyFrom.item5"),
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-600">
                    <Check className="text-gold mt-1 flex-shrink-0" size={18} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="bg-slate-50 p-8">
                <h3 className="font-outfit text-xl font-semibold text-navy mb-6">
                  {t("sellPanels.whatWeAccept.title")}
                </h3>
                <ul className="space-y-3 mb-8">
                  {[
                    t("sellPanels.whatWeAccept.item1"),
                    t("sellPanels.whatWeAccept.item2"),
                    t("sellPanels.whatWeAccept.item3"),
                    t("sellPanels.whatWeAccept.item4"),
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-600">
                      <Check className="text-green-600 mt-1 flex-shrink-0" size={18} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <h3 className="font-outfit text-xl font-semibold text-navy mb-6">
                  {t("sellPanels.whatWeDoNotAccept.title")}
                </h3>
                <ul className="space-y-3">
                  {[
                    t("sellPanels.whatWeDoNotAccept.item1"),
                    t("sellPanels.whatWeDoNotAccept.item2"),
                    t("sellPanels.whatWeDoNotAccept.item3"),
                    t("sellPanels.whatWeDoNotAccept.item4"),
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-600">
                      <X className="text-red-500 mt-1 flex-shrink-0" size={18} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
              {t("sellPanels.process.kicker")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mt-4">
              {t("sellPanels.process.title")}
            </h2>
          </div>

          <div className="max-w-2xl mx-auto">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <ProcessStep number={index + 1} {...step} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
              {t("sellPanels.whyChooseUs.kicker")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mt-4">
              {t("sellPanels.whyChooseUs.title")}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: t("sellPanels.whyChooseUs.card1.title"), desc: t("sellPanels.whyChooseUs.card1.desc") },
              { title: t("sellPanels.whyChooseUs.card2.title"), desc: t("sellPanels.whyChooseUs.card2.desc") },
              { title: t("sellPanels.whyChooseUs.card3.title"), desc: t("sellPanels.whyChooseUs.card3.desc") },
              { title: t("sellPanels.whyChooseUs.card4.title", { years: COMPANY_STATS.yearsExperience }), desc: t("sellPanels.whyChooseUs.card4.desc") },
              { title: t("sellPanels.whyChooseUs.card5.title"), desc: t("sellPanels.whyChooseUs.card5.desc") },
              { title: t("sellPanels.whyChooseUs.card6.title"), desc: t("sellPanels.whyChooseUs.card6.desc") },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card-hover bg-white border border-slate-200 p-6"
              >
                <Package className="text-gold w-8 h-8 mb-4" strokeWidth={1.5} />
                <h3 className="font-outfit font-semibold text-navy mb-1">{item.title}</h3>
                <p className="text-slate-500 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 md:py-32 bg-navy text-white relative noise-overlay">
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
                {t("sellPanels.form.kicker")}
              </span>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold mt-4 mb-6">
                {t("sellPanels.form.title")}
              </h2>
              <p className="text-slate-300 mb-8 leading-relaxed">
                {t("sellPanels.form.subtitle")}
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-3 text-gold">
                  <CheckCircle size={18} />
                  <span>{t("sellPanels.form.feature1")}</span>
                </div>
                <div className="flex items-center gap-3 text-gold">
                  <CheckCircle size={18} />
                  <span>{t("sellPanels.form.feature2")}</span>
                </div>
                <div className="flex items-center gap-3 text-gold">
                  <CheckCircle size={18} />
                  <span>{t("sellPanels.form.feature3")}</span>
                </div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <form onSubmit={handleSubmit} className="bg-white p-8 text-navy" data-testid="uk-enquiry-form">
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    name="name"
                    placeholder={t("sellPanels.form.namePlaceholder")}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200 text-navy placeholder-slate-400"
                    data-testid="uk-form-name"
                  />
                  <input
                    type="text"
                    name="company"
                    placeholder={t("sellPanels.form.companyPlaceholder")}
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200 text-navy placeholder-slate-400"
                    data-testid="uk-form-company"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <input
                    type="tel"
                    name="phone"
                    placeholder={t("sellPanels.form.phonePlaceholder")}
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200 text-navy placeholder-slate-400"
                    data-testid="uk-form-phone"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder={t("sellPanels.form.emailPlaceholder")}
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200 text-navy placeholder-slate-400"
                    data-testid="uk-form-email"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    name="panel_count"
                    placeholder={t("sellPanels.form.panelCountPlaceholder")}
                    value={formData.panel_count}
                    onChange={handleChange}
                    className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200 text-navy placeholder-slate-400"
                    data-testid="uk-form-panel-count"
                  />
                  <input
                    type="text"
                    name="panel_brand"
                    placeholder={t("sellPanels.form.panelBrandPlaceholder")}
                    value={formData.panel_brand}
                    onChange={handleChange}
                    className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200 text-navy placeholder-slate-400"
                    data-testid="uk-form-panel-brand"
                  />
                </div>
                <input
                  type="text"
                  name="location"
                  placeholder={t("sellPanels.form.locationPlaceholder")}
                  value={formData.location}
                  onChange={handleChange}
                  className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200 text-navy placeholder-slate-400 mb-4"
                  data-testid="uk-form-location"
                />
                <textarea
                  name="message"
                  placeholder={t("sellPanels.form.messagePlaceholder")}
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200 text-navy placeholder-slate-400 mb-6 resize-none"
                  data-testid="uk-form-message"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="uk-form-submit"
                  className="w-full bg-gold text-navy px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t("sellPanels.form.submittingButton") : t("sellPanels.form.submitButton")}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SellPanelsPage;
